import skillDatabaseSchema from '../assets/data-schemas/skillDatabaseSchema.json';
import workflowBuilderSkillSeed from '../assets/data-schemas/workflow_builder_skill.json';
import { createDatabaseTable } from './customdb.service';
import { addDatabaseItemByVersion } from './axios/data.api';
import { formatTablename, getCETTimeStampFromDate } from '../utils/helpers';

const DEFAULT_LAYOUT =
  '{"tabs":[{"id":0,"rows":[{"id":0,"cols":[{"id":0}]}]}]}';

const REQUIRED_DATABASES = [
  {
    key: 'ai-skills',
    caption: 'AI-Skills',
    tableSuffix: 'ai-skills',
    tabletype: 'custom-ai-skills',
    type: 'custom-ai-skills',
    pagination: true,
    schema: skillDatabaseSchema,
  },
];

const normalizeSchema = (schema = []) => {
  if (!Array.isArray(schema)) return [];
  if (Array.isArray(schema[0])) return schema[0];
  return schema;
};

const attachSchemaIds = (schema = []) => {
  return normalizeSchema(schema).map((field, index) => ({
    ...field,
    id: getCETTimeStampFromDate() + (index + 1),
  }));
};

const resolveTableName = (username, tableSuffix) =>
  `${username}_${formatTablename(tableSuffix)}`;

const serializeSkillContext = (context) => {
  if (typeof context === 'string') return context;
  if (context === null || context === undefined) return '';
  try {
    return JSON.stringify(context);
  } catch (error) {
    return '';
  }
};

const seedInitialAISkillItem = async (username, databaseConfig) => {
  if (databaseConfig?.key !== 'ai-skills') return;

  const tableName = resolveTableName(username, databaseConfig.tableSuffix);
  const seededSkillName =
    workflowBuilderSkillSeed?.skillname || 'workflow_builder_skill';
  const seededSkillContext =
    workflowBuilderSkillSeed?.skillcontext || 'Workflow builder skill';
  const payload = {
    item: {
      skillname: seededSkillName,
      skillcontext: JSON.parse(JSON.stringify(seededSkillContext)),
    },
  };

  try {
    await addDatabaseItemByVersion(tableName, payload, 'v8');
  } catch (error) {
    // Keep DB creation successful even if seed insert fails.
  }
};

const buildCreateDatabaseFormData = (username, databaseConfig) => {
  const formData = new FormData();
  formData.append('caption', databaseConfig.caption);
  formData.append('pagination', databaseConfig.pagination || false);
  formData.append('add_only', false);
  formData.append('icon', null);
  formData.append(
    'tablename',
    resolveTableName(username, databaseConfig.tableSuffix)
  );
  formData.append('tabletype', databaseConfig.tabletype);
  formData.append('layout', DEFAULT_LAYOUT);
  formData.append('confirmDelete', true);
  formData.append('showSeconds', true);
  formData.append('database_type', 'DynamoDB');
  formData.append('type', databaseConfig.type);
  formData.append(
    'schema',
    JSON.stringify(attachSchemaIds(databaseConfig.schema))
  );
  return formData;
};

const isDatabaseMatched = (database, databaseConfig, username) => {
  if (!database) return false;

  const targetTableName = resolveTableName(
    username,
    databaseConfig.tableSuffix
  );
  const dbCaption = `${database.caption || ''}`.toLowerCase();

  return (
    database.name === targetTableName ||
    database.tablename === targetTableName ||
    database.tabletype === databaseConfig.tabletype ||
    database.type === databaseConfig.type ||
    dbCaption === databaseConfig.caption.toLowerCase()
  );
};

const isAlreadyExistsError = (error) => {
  const statusCode = error?.response?.status;
  const message = `${
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    ''
  }`.toLowerCase();

  return (
    statusCode === 409 ||
    message.includes('already exists') ||
    message.includes('already exist')
  );
};

export const ensureRequiredDatabases = async ({
  username,
  databases = [],
  allowAISkillDatabase = true,
}) => {
  if (!username) return { created: [] };

  const created = [];

  for (const databaseConfig of REQUIRED_DATABASES) {
    if (databaseConfig?.key === 'ai-skills' && !allowAISkillDatabase) {
      continue;
    }

    const isExisting = databases.some((database) =>
      isDatabaseMatched(database, databaseConfig, username)
    );

    if (isExisting) continue;

    try {
      const formData = buildCreateDatabaseFormData(username, databaseConfig);
      await createDatabaseTable(formData);
      await seedInitialAISkillItem(username, databaseConfig);
      created.push(databaseConfig.key);
    } catch (error) {
      if (!isAlreadyExistsError(error)) {
        throw error;
      }
    }
  }

  return { created };
};

export { REQUIRED_DATABASES };
